import {
  activeItems,
  type Config,
  DATE_PRESET_LABELS,
  type DatePreset,
  fmtDate,
  hasActiveFilters,
  type PersonRef,
  parseIsoDate,
  presetFor,
  presetRange,
  type QueueFilters,
  type QueueListFilter,
  STAGE_META,
  STAGES,
  type Stage,
  UNASSIGNED_FILTER,
} from '@hrms/shared'
import { type ReactNode, useEffect, useId, useMemo, useRef, useState } from 'react'
import type { IconType } from 'react-icons'
import {
  LuActivity,
  LuAlarmClock,
  LuCalendarRange,
  LuCircleCheck,
  LuCircleDashed,
  LuHourglass,
  LuInbox,
  LuLayers,
  LuSearch,
  LuSignal,
  LuTag,
  LuUser,
  LuUserCheck,
} from 'react-icons/lu'
import { TbFilterPlus } from 'react-icons/tb'
import {
  AddFilterMenu,
  Avatar,
  Button,
  Chip,
  FilterChip,
  type FilterField,
  IconButton,
  Lozenge,
  type OptionItem,
  OptionList,
  TextInput,
} from '../../../components/ui/index.ts'
import { cn } from '../../../lib/cn.ts'
import { hint } from '../../../lib/hint.ts'
import { PersonAvatar, PriorityBars } from '../tickets/ui.tsx'
import type { FilterUpdate } from './useQueueParams.ts'

const SEARCH_DEBOUNCE_MS = 300
const FORMER_MEMBER = 'Former member'

/** Stage icons, shared with the summary tiles. */
export const STAGE_ICONS: Record<Stage, { icon: IconType; iconClass: string }> = {
  new: { icon: LuInbox, iconClass: 'text-blue-fg' },
  in_progress: { icon: LuActivity, iconClass: 'text-violet-fg' },
  waiting: { icon: LuHourglass, iconClass: 'text-amber-fg' },
  done: { icon: LuCircleCheck, iconClass: 'text-green-fg' },
}

type FieldId = QueueListFilter | 'created' | 'overdue'

const FIELD_META: Record<FieldId, { label: string; icon: IconType; iconClass: string }> = {
  status: { label: 'Status', icon: LuCircleDashed, iconClass: 'text-violet-fg' },
  stage: { label: 'Stage', icon: LuLayers, iconClass: 'text-amber-fg' },
  priority: { label: 'Priority', icon: LuSignal, iconClass: 'text-orange-fg' },
  category: { label: 'Category', icon: LuTag, iconClass: 'text-teal-fg' },
  raisedBy: { label: 'Raised by', icon: LuUser, iconClass: 'text-blue-fg' },
  assignee: { label: 'Assignee', icon: LuUserCheck, iconClass: 'text-green-fg' },
  created: { label: 'Created', icon: LuCalendarRange, iconClass: 'text-blue-fg' },
  overdue: { label: 'Overdue', icon: LuAlarmClock, iconClass: 'text-rose-fg' },
}

/** Chip order (and the order of the field list). */
const FIELD_ORDER: FieldId[] = [
  'status',
  'stage',
  'priority',
  'category',
  'raisedBy',
  'assignee',
  'created',
  'overdue',
]

const PLURALS: Record<QueueListFilter, string> = {
  status: 'statuses',
  stage: 'stages',
  priority: 'priorities',
  category: 'categories',
  raisedBy: 'people',
  assignee: 'people',
}

/** "High", "High, Urgent", or "3 priorities" when the names get long. */
export function summarize(names: string[], plural: string): string {
  if (names.length === 1) return names[0] ?? ''
  const joined = names.join(', ')
  return names.length === 2 && joined.length <= 22 ? joined : `${names.length} ${plural}`
}

const firstName = (name: string) => name.split(/\s+/)[0] ?? name

function isoLabel(iso: string, now: number) {
  const d = parseIsoDate(iso)
  return d ? fmtDate(Date.UTC(d.year, d.month - 1, d.day, 12), { timeZone: 'UTC', now }) : iso
}

/** Small coloured dot in a palette colour. */
function Dot({ color }: { color: string }) {
  return (
    <span
      aria-hidden
      data-color={color}
      className="inline-block size-2 shrink-0 rounded-full bg-cf"
    />
  )
}

/** Up to three overlapping visuals in a chip value. */
function Stack({ children }: { children: ReactNode[] }) {
  return (
    <span
      aria-hidden
      className="inline-flex shrink-0 items-center -space-x-0.5 [&>*]:ring-2 [&>*]:ring-surface"
    >
      {children.slice(0, 3)}
    </span>
  )
}

/**
 * Compact queue filters (Linear/Plane style), shared by the list and the board (which has no
 * Status or Stage): search, one chip per active filter, an "Add filter" button and Clear.
 * Everything lives in the URL (`useQueueParams`). DESIGN.md §3.1.
 */
export function QueueFilterBar({
  filters,
  setFilters,
  clearFilters,
  config,
  people,
  timeZone,
  now,
  hideStatus,
}: {
  filters: QueueFilters
  setFilters: (patch: FilterUpdate, opts?: { replace?: boolean }) => void
  clearFilters: () => void
  config: Config
  people: PersonRef[]
  timeZone: string | undefined
  now: number
  /** The board: no Status or Stage filter (columns already split by status). */
  hideStatus?: boolean
}) {
  const searchId = useId()

  // Search: local text, pushed to the URL after a pause (replacing the history entry).
  const [text, setText] = useState(filters.q ?? '')
  const sent = useRef(filters.q ?? '')
  useEffect(() => {
    // The URL changed from elsewhere (back/forward, Clear): follow it.
    if ((filters.q ?? '') !== sent.current) {
      sent.current = filters.q ?? ''
      setText(sent.current)
    }
  }, [filters.q])
  useEffect(() => {
    if (text.trim() === sent.current) return
    const t = window.setTimeout(() => {
      sent.current = text.trim()
      setFilters({ q: text.trim() }, { replace: true })
    }, SEARCH_DEBOUNCE_MS)
    return () => window.clearTimeout(t)
  }, [text, setFilters])

  // One popover at a time: the field list (from Add filter) or one field's values (from a chip).
  const [menu, setMenu] = useState<{ field?: FieldId } | null>(null)
  const anchorRef = useRef<HTMLElement | null>(null)
  const addRef = useRef<HTMLButtonElement>(null)
  const chipRefs = useRef<Partial<Record<FieldId, HTMLButtonElement | null>>>({})
  // The first change in a popover session pushes a history entry; the rest replace it, so Back
  // undoes the whole edit rather than one tick at a time.
  const pushed = useRef(false)
  const apply = (patch: FilterUpdate) => {
    setFilters(patch, { replace: pushed.current })
    pushed.current = true
  }
  const open = (field: FieldId | undefined, el: HTMLElement | null) => {
    if (menu && menu.field === field && anchorRef.current === el) return setMenu(null)
    anchorRef.current = el
    pushed.current = false
    setMenu({ field })
  }

  // ---- Options ------------------------------------------------------------------------------
  const hrPeople = useMemo(() => people.filter((p) => p.role === 'admin'), [people])
  const byId = useMemo(() => new Map(people.map((p) => [p.id, p])), [people])
  const list = (k: QueueListFilter): string[] =>
    hideStatus && (k === 'status' || k === 'stage') ? [] : (filters[k] ?? [])

  const toggle = (k: QueueListFilter, value: string) =>
    apply((current) => {
      const cur = current[k] ?? []
      return { [k]: cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value] }
    })

  /** Configured items (active first, archived after) plus any selected id that's gone. */
  function configOptions<T extends { id: string; name: string; archived?: boolean }>(
    k: QueueListFilter,
    items: readonly T[],
    render: (item: T) => Partial<OptionItem>,
    opts: { includeArchived?: boolean } = {},
  ): OptionItem[] {
    const sel = list(k)
    const shown = items.filter((i) => opts.includeArchived || !i.archived || sel.includes(i.id))
    const ordered = [...shown.filter((i) => !i.archived), ...shown.filter((i) => i.archived)]
    const out: OptionItem[] = ordered.map((i) => ({
      id: i.id,
      label: i.name,
      selected: sel.includes(i.id),
      meta: i.archived ? 'archived' : undefined,
      ...render(i),
    }))
    for (const id of sel)
      if (!items.some((i) => i.id === id))
        out.push({ id, label: `Unknown (${id})`, selected: true })
    return out
  }

  function personOptions(k: 'raisedBy' | 'assignee', pool: PersonRef[]): OptionItem[] {
    const sel = list(k)
    const out: OptionItem[] = []
    if (k === 'assignee')
      out.push({
        id: UNASSIGNED_FILTER,
        label: 'Unassigned',
        keywords: 'nobody none',
        leading: <Avatar size="xs" />,
        selected: sel.includes(UNASSIGNED_FILTER),
      })
    for (const p of pool)
      out.push({
        id: p.id,
        label: p.name,
        keywords: p.dept ?? undefined,
        leading: <PersonAvatar person={p} size="xs" />,
        selected: sel.includes(p.id),
      })
    for (const id of sel)
      if (id !== UNASSIGNED_FILTER && !pool.some((p) => p.id === id))
        out.push({
          id,
          label: byId.get(id)?.name ?? FORMER_MEMBER,
          leading: <PersonAvatar person={byId.get(id)} id={id} size="xs" />,
          selected: true,
        })
    return out
  }

  const statuses = config.statuses
  const priorities = config.priorities
  const categories = config.categories

  const options: Record<QueueListFilter, OptionItem[]> = {
    status: configOptions('status', statuses, (s) => ({
      render: <Lozenge color={s.color}>{s.name}</Lozenge>,
      keywords: STAGE_META[s.stage].label,
    })),
    stage: STAGES.map((s) => {
      const I = STAGE_ICONS[s]
      return {
        id: s,
        label: STAGE_META[s].label,
        leading: <I.icon className={I.iconClass} />,
        meta: activeItems(statuses)
          .filter((x) => x.stage === s)
          .map((x) => x.name)
          .join(', '),
        selected: list('stage').includes(s),
      }
    }),
    // Highest first, like the PriorityBars read.
    priority: configOptions('priority', [...priorities].reverse(), (p) => ({
      render: <PriorityBars priorityId={p.id} priorities={priorities} />,
    })),
    category: configOptions(
      'category',
      categories,
      (c) => ({
        render: (
          <Chip color={c.color} dot>
            {c.name}
          </Chip>
        ),
      }),
      { includeArchived: true },
    ),
    raisedBy: personOptions('raisedBy', people),
    assignee: personOptions('assignee', hrPeople),
  }

  // ---- Created date -------------------------------------------------------------------------
  const datePreset: DatePreset = presetFor(filters.createdFrom, filters.createdTo, now, timeZone)
  const dateActive = !!(filters.createdFrom || filters.createdTo)
  const dateSummary = (() => {
    if (datePreset !== 'custom' && datePreset !== 'any')
      return { op: 'is', text: DATE_PRESET_LABELS[datePreset] }
    const { createdFrom: f, createdTo: t } = filters
    if (f && t) return { op: 'between', text: `${isoLabel(f, now)} – ${isoLabel(t, now)}` }
    if (f) return { op: 'from', text: isoLabel(f, now) }
    return { op: 'until', text: isoLabel(t ?? '', now) }
  })()

  // ---- Fields -------------------------------------------------------------------------------
  const fields: FilterField[] = FIELD_ORDER.filter(
    (id) => !(hideStatus && (id === 'status' || id === 'stage')),
  ).map((id): FilterField => {
    const m = FIELD_META[id]
    if (id === 'overdue')
      return {
        id,
        ...m,
        kind: 'toggle',
        keywords: 'late past eta',
        active: !!filters.overdue,
        onToggle: () => apply((current) => ({ overdue: current.overdue ? '' : '1' })),
      }
    if (id === 'created')
      return {
        id,
        ...m,
        label: 'Created date',
        kind: 'custom',
        keywords: 'raised when date today week month range',
        active: dateActive,
        activeMeta: dateSummary.text,
        render: ({ close, back }) => (
          <DatePanel
            preset={datePreset}
            from={filters.createdFrom}
            to={filters.createdTo}
            onBack={back}
            onPreset={(p) => {
              const r = presetRange(p, now, timeZone)
              apply({ createdFrom: r?.from, createdTo: r?.to })
              close()
            }}
            onRange={(from, to) => apply({ createdFrom: from, createdTo: to })}
            onDone={close}
          />
        ),
      }
    const n = list(id).length
    return {
      id,
      ...m,
      kind: 'multi',
      active: n > 0,
      activeMeta: n > 0 ? String(n) : undefined,
      options: options[id],
      onToggle: (v) => toggle(id, v),
      placeholder: id === 'raisedBy' || id === 'assignee' ? 'Search people' : undefined,
    }
  })

  // ---- Chips --------------------------------------------------------------------------------
  function chipValue(k: QueueListFilter): { node: ReactNode; text: string } {
    const sel = list(k)
    const many = sel.length > 1
    const nameOf = (id: string): string => {
      switch (k) {
        case 'status':
          return statuses.find((s) => s.id === id)?.name ?? 'Unknown'
        case 'stage':
          return STAGE_META[id as Stage]?.label ?? id
        case 'priority':
          return priorities.find((p) => p.id === id)?.name ?? 'Unknown'
        case 'category':
          return categories.find((c) => c.id === id)?.name ?? 'Unknown'
        default: {
          if (id === UNASSIGNED_FILTER) return 'Unassigned'
          const name = byId.get(id)?.name ?? FORMER_MEMBER
          return many ? firstName(name) : name
        }
      }
    }
    const text = summarize(sel.map(nameOf), PLURALS[k])
    let lead: ReactNode = null
    if (k === 'status')
      lead = (
        <Stack>
          {sel.map((id) => (
            <Dot key={id} color={statuses.find((s) => s.id === id)?.color ?? 'slate'} />
          ))}
        </Stack>
      )
    else if (k === 'category')
      lead = (
        <Stack>
          {sel.map((id) => (
            <Dot key={id} color={categories.find((c) => c.id === id)?.color ?? 'slate'} />
          ))}
        </Stack>
      )
    else if (k === 'stage') {
      const I = STAGE_ICONS[sel[0] as Stage]
      if (!many && I) lead = <I.icon aria-hidden className={cn('size-3.5', I.iconClass)} />
    } else if (k === 'priority') {
      if (!many && sel[0])
        lead = <PriorityBars priorityId={sel[0]} priorities={priorities} showName={false} />
    } else
      lead = (
        <Stack>
          {sel.map((id) =>
            id === UNASSIGNED_FILTER ? (
              <Avatar key={id} size="xs" />
            ) : (
              <PersonAvatar key={id} person={byId.get(id)} id={id} size="xs" />
            ),
          )}
        </Stack>
      )
    return {
      node: (
        <>
          {lead}
          <span className="truncate">{text}</span>
        </>
      ),
      text,
    }
  }

  const chips = FIELD_ORDER.flatMap((id) => {
    const m = FIELD_META[id]
    const common = {
      icon: m.icon,
      iconClass: m.iconClass,
      label: m.label,
      expanded: menu?.field === id,
    }
    if (id === 'overdue')
      return filters.overdue
        ? [<FilterChip key={id} {...common} onRemove={() => setFilters({ overdue: '' })} />]
        : []
    if (id === 'created') {
      if (!dateActive) return []
      return [
        <FilterChip
          key={id}
          {...common}
          operator={dateSummary.op}
          value={dateSummary.text}
          valueText={dateSummary.text}
          editRef={(el) => {
            chipRefs.current.created = el
          }}
          onEdit={() => open('created', chipRefs.current.created ?? null)}
          onRemove={() => setFilters({ createdFrom: '', createdTo: '' })}
        />,
      ]
    }
    const sel = list(id)
    if (sel.length === 0) return []
    const v = chipValue(id)
    return [
      <FilterChip
        key={id}
        {...common}
        operator={sel.length > 1 ? 'is any of' : 'is'}
        value={v.node}
        valueText={v.text}
        editRef={(el) => {
          chipRefs.current[id] = el
        }}
        onEdit={() => open(id, chipRefs.current[id] ?? null)}
        onRemove={() => setFilters({ [id]: '' })}
      />,
    ]
  })

  const visibleFilters: QueueFilters = hideStatus
    ? { ...filters, status: undefined, stage: undefined }
    : filters

  return (
    <search
      data-tour="queue-filters"
      className="mb-3 flex flex-wrap items-center gap-1.5"
      aria-label="Filter tickets"
    >
      <span className="relative inline-flex w-[220px] max-[560px]:w-auto max-[560px]:min-w-[140px] max-[560px]:flex-1">
        <label htmlFor={searchId} className="sr-only">
          Search tickets
        </label>
        <LuSearch
          aria-hidden
          className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-muted"
        />
        <TextInput
          id={searchId}
          type="search"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Search by ID, title or name"
          size="xs"
          className="pl-7.5"
          {...hint('Matches the ticket ID, title, description and the name of who raised it')}
        />
      </span>

      {chips}

      <IconButton
        ref={addRef}
        size="sm"
        aria-label="Add filter"
        aria-haspopup="dialog"
        aria-expanded={!!menu && menu.field === undefined}
        onClick={() => open(undefined, addRef.current)}
        data-tour="queue-add-filter"
        className={cn(
          'border border-dashed border-border-strong text-muted hover:border-faint',
          menu && menu.field === undefined && 'bg-surface-2 text-text',
        )}
        {...hint('Add filter')}
      >
        <TbFilterPlus aria-hidden className="size-4" />
      </IconButton>

      {hasActiveFilters(visibleFilters) && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setMenu(null)
            clearFilters()
          }}
          {...hint('Remove every filter and the search')}
        >
          Clear
        </Button>
      )}

      {menu && (
        <AddFilterMenu
          key={menu.field ?? 'add'}
          anchor={anchorRef}
          returnFocus={addRef}
          fields={fields}
          fieldId={menu.field}
          onClose={() => setMenu(null)}
        />
      )}
    </search>
  )
}

const PRESETS = ['today', '7d', '30d', 'month'] as const

/** Created date: presets (one click) or a custom From/To range. */
function DatePanel({
  preset,
  from,
  to,
  onBack,
  onPreset,
  onRange,
  onDone,
}: {
  preset: DatePreset
  from: string | undefined
  to: string | undefined
  onBack?: () => void
  onPreset: (p: DatePreset) => void
  onRange: (from: string, to: string) => void
  onDone: () => void
}) {
  const [custom, setCustom] = useState(preset === 'custom')
  const ids = { from: useId(), to: useId() }
  const fromRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (custom) fromRef.current?.focus()
  }, [custom])

  if (!custom)
    return (
      <OptionList
        label="Created date"
        placeholder="Search dates"
        onBack={onBack}
        backLabel="Created date"
        onEmptyBackspace={onBack}
        items={[
          ...PRESETS.map((p) => ({ id: p, label: DATE_PRESET_LABELS[p], selected: preset === p })),
          {
            id: 'custom',
            label: 'Custom range',
            keywords: 'between from to dates',
            submenu: true,
            selected: preset === 'custom',
          },
        ]}
        onPick={(id) => (id === 'custom' ? setCustom(true) : onPreset(id as DatePreset))}
      />
    )

  return (
    <form
      className="flex flex-col gap-2 p-2.5"
      onSubmit={(e) => {
        e.preventDefault()
        onDone()
      }}
    >
      <button
        type="button"
        onClick={() => setCustom(false)}
        className="-mx-2.5 -mt-2.5 mb-0.5 flex items-center gap-1 border-b border-border px-2.5 py-1.5 text-left text-[12.5px] font-medium text-muted hover:bg-surface-2 hover:text-text"
      >
        <LuCalendarRange aria-hidden className="size-3.5 text-blue-fg" />
        Custom range
      </button>
      <div className="grid grid-cols-[auto_1fr] items-center gap-x-2 gap-y-1.5 text-[12.5px]">
        <label htmlFor={ids.from} className="text-muted">
          From
        </label>
        <TextInput
          ref={fromRef}
          id={ids.from}
          type="date"
          value={from ?? ''}
          max={to}
          onChange={(e) => onRange(e.target.value, to ?? '')}
          size="xs"
        />
        <label htmlFor={ids.to} className="text-muted">
          To
        </label>
        <TextInput
          id={ids.to}
          type="date"
          value={to ?? ''}
          min={from}
          onChange={(e) => onRange(from ?? '', e.target.value)}
          size="xs"
        />
      </div>
      <p className="text-xs text-muted">
        Both days are included. Leave one empty for an open range.
      </p>
      <div className="flex justify-end">
        <Button type="submit" size="sm" variant="primary">
          Done
        </Button>
      </div>
    </form>
  )
}
